import React, { createContext, useContext, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import { useWorkspace } from './WorkspaceContext';
import { Equipment, MaintenanceLog } from '../types';
import {
    useEquipment, useMaintenanceLogs,
    useAddEquipment, useUpdateEquipment, useDeleteEquipment,
    useLogMaintenance
} from '../hooks/useFleetQueries';

interface FleetContextType {
    equipment: Equipment[];
    maintenanceLogs: MaintenanceLog[];
    addEquipment: (item: Omit<Equipment, 'id'>) => Promise<void>;
    updateEquipment: (id: string, updates: Partial<Equipment>) => Promise<void>;
    deleteEquipment: (id: string) => Promise<void>;
    logMaintenance: (log: Omit<MaintenanceLog, 'id'>) => Promise<void>;
}

const FleetContext = createContext<FleetContextType | undefined>(undefined);

export const useFleet = () => {
    const context = useContext(FleetContext);
    if (!context) {
        throw new Error('useFleet must be used within a FleetProvider');
    }
    return context;
};

export const FleetProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { isAuthorized } = useAuth();
    const { activeWorkspace } = useWorkspace();
    const { data: equipment = [] } = useEquipment();
    const { data: maintenanceLogs = [] } = useMaintenanceLogs();

    const addEquipmentMut = useAddEquipment();
    const updateEquipmentMut = useUpdateEquipment();
    const deleteEquipmentMut = useDeleteEquipment();
    const logMaintenanceMut = useLogMaintenance();

    const addEquipment = async (item: Omit<Equipment, 'id'>) => {
        await addEquipmentMut.mutateAsync(item);
    };

    const updateEquipment = async (id: string, updates: Partial<Equipment>) => {
        await updateEquipmentMut.mutateAsync({ id, updates });
    };

    const deleteEquipment = async (id: string) => {
        await deleteEquipmentMut.mutateAsync(id);
    };

    const logMaintenance = async (log: Omit<MaintenanceLog, 'id'>) => {
        await logMaintenanceMut.mutateAsync(log);
    };

    return (
        <FleetContext.Provider value={{
            equipment, maintenanceLogs, addEquipment, updateEquipment, deleteEquipment, logMaintenance
        }}>
            {children}
        </FleetContext.Provider>
    );
};
